import { Repository, SearchFilters } from '../../repository';
import { Listing, Review, UserProfile, ListingStatus, Vendor } from '../../../types';
import { D1ListingsRepo } from './listings.repo';

import { D1ReviewsRepo } from './reviews.repo';
import { D1ProfileRepo } from './profile.repo';
import { D1FavoritesRepo } from './favorites.repo';
import { D1MiscRepo } from './misc.repo';
import { D1VendorRepository } from './vendors.repo';

export class D1Repository implements Repository {
    private listings = new D1ListingsRepo();

    private reviews = new D1ReviewsRepo();
    private profile = new D1ProfileRepo();
    private favorites = new D1FavoritesRepo();
    private misc = new D1MiscRepo();
    private vendors = new D1VendorRepository();

    // Listings
    getListingById(id: string) { return this.listings.getListingById(id); }
    getListings(filters?: SearchFilters) { return this.listings.getListings(filters); }
    getListingsCount() { return this.listings.getListingsCount(); }
    getHomepageListings() { return this.listings.getHomepageListings(); }
    getAdminListings() { return this.listings.getAdminListings(); }
    getMyListings(userId: string) { return this.listings.getMyListings(userId); }
    addListing(listing: Listing, userId: string) { return this.listings.addListing(listing, userId); }
    updateListing(listing: Listing, userId: string) { return this.listings.updateListing(listing, userId); }
    updateListingStatus(id: string, status: ListingStatus) { return this.listings.updateListingStatus(id, status); }
    updateListingTrust(id: string, fields: Record<string, string | boolean | null>) { return this.listings.updateListingTrust(id, fields); }
    updateListingImages(id: string, images: string[]) { return this.listings.updateListingImages(id, images); }
    deleteListing(id: string) { return this.listings.deleteListing(id); }
    toggleListingStatus(id: string, current: string): Promise<{ success: boolean; error?: string }> { return this.listings.toggleListingStatus(id, current); }
    updateListingProfileRequirement(sid: string, req: boolean): Promise<{ success: boolean; error?: string }> { return this.listings.updateListingProfileRequirement(sid, req); }

    // Vendors
    getVendorById(id: string) { return this.vendors.getVendorById(id); }
    getVendors(filters?: any) { return this.vendors.getVendors(filters); }
    getMyVendors(userId: string) { return this.vendors.getMyVendors(userId); }
    addVendor(vendor: Vendor, userId: string) { return this.vendors.addVendor(vendor, userId); }
    updateVendor(vendor: Vendor, userId: string) { return this.vendors.updateVendor(vendor, userId); }
    deleteVendor(id: string) { return this.vendors.deleteVendor(id); }

    // Analytics
    getListingAnalytics(id: string, days?: number) { return this.listings.getListingAnalytics(id, days); }
    getListingsAnalyticsBulk(ids: string[], days?: number) { return this.listings.getListingsAnalyticsBulk(ids, days); }
    trackListingClick(id: string) { return this.listings.trackListingClick(id); }

    // Reviews
    getReviewsForListing(id: string) { return this.reviews.getReviewsForListing(id); }
    getMyReviewForListing(uid: string, lid: string) { return this.reviews.getMyReviewForListing(uid, lid); }
    canReviewListing(uid: string, lid: string) {
        return this.reviews.getMyReviewForListing(uid, lid).then(r => !r);
    }
    createReview(params: Omit<Review, 'id' | 'createdAt'>) { return this.reviews.createReview(params); }



    // Favorites
    isFavorite(uid: string, lid: string) { return this.favorites.isFavorite(uid, lid); }
    addFavorite(uid: string, lid: string) { return this.favorites.addFavorite(uid, lid); }
    removeFavorite(uid: string, lid: string) { return this.favorites.removeFavorite(uid, lid); }
    getFavorites(uid: string) { return this.favorites.getFavorites(uid); }
    async getFavoriteListings(uid: string) {
        const ids = await this.favorites.getFavorites(uid);
        if (ids.length === 0) return [];
        return await this.listings.getFavoriteListingsByIds(ids);
    }

    // Profile
    getUserProfile() { return this.profile.getUserProfile(); }
    updateUserProfile(p: Partial<UserProfile>) { return this.profile.updateUserProfile(p); }
    getAllUserProfiles() { return this.profile.getAllUserProfiles(); }
    syncAuth(data: any) { return this.profile.syncAuth(data); }

    async deleteUserListings(uid: string) {
        const mine = await this.listings.getMyListings(uid);
        let count = 0;
        for (const l of mine) {
            const res = await this.listings.deleteListing(l.id);
            if (res.success) count++;
        }
        return count;
    }

    async deleteAccountData(uid: string) {
        // Complex cleanup logic using multiple sub-repos
        const mine = await this.listings.getMyListings(uid);
        for (const l of mine) await this.listings.deleteListing(l.id);



        const favorites = await this.favorites.getFavorites(uid);
        for (const f of favorites) await this.favorites.removeFavorite(uid, f);

        return true;
    }

    // Misc
    getBlogPosts() { return this.misc.getBlogPosts(); }
    getBlogPost(id: number) { return this.misc.getBlogPost(id); }
    getSetting(k: string) { return this.misc.getSetting(k); }
    setSetting(k: string, v: any) { return this.misc.setSetting(k, v); }
}
